import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle, Search, Zap, DollarSign, Clock, Database } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const FAQ = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const categoryRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Add page-specific meta tags for SEO
  usePageMeta({
    title: "FAQ - Frequently Asked Questions | Let's Be Real",
    description: "Common questions about Let's Be Real property reports. Learn about our data sources, accuracy, pricing, and how we provide brutally honest Australian property insights.",
    canonical: "https://letsbereal.com.au/faq",
    ogImage: "https://letsbereal.com.au/lbr.png",
    ogType: "website",
    twitterCard: "summary",
    keywords: "FAQ, frequently asked questions, property reports help, data accuracy, pricing, australian property analysis, census 2021, HERE API",
    schema: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "name": "Frequently Asked Questions",
      "description": "Common questions about Let's Be Real property analysis platform",
      "url": "https://letsbereal.com.au/faq",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How accurate is your data?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We use official sources and enterprise APIs: Census 2021 data, SEIFA socio-economic indexes, HERE APIs (real-time transit, places, routing), OpenStreetMap for roads, and ChatGPT for AI analysis. We never make up numbers. Data combines government statistics with live real-time information."
          }
        },
        {
          "@type": "Question",
          "name": "Is this service free?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Completely free, no signup required, no credit card needed. We believe honest property insights should be accessible to everyone making important housing decisions."
          }
        }
      ]
    }
  });

  const faqs = [
    {
      category: "About the Service",
      questions: [
        {
          question: "How accurate is your data?",
          answer: "We use official sources and enterprise-grade APIs: Census 2021 data, SEIFA socio-economic indexes, HERE APIs (real-time transit, places, routing), OpenStreetMap for road infrastructure, and AI analysis. We never make up numbers. Our data combines government statistics (Census 2021) with live, real-time information (transit schedules, nearby amenities). However, data is only as current as its source."
        },
        {
          question: "Is this service free?",
          answer: "Yes! Completely free, no signup required, no credit card needed. We believe honest property insights should be accessible to everyone making important housing decisions."
        },
        {
          question: "Why is the report so brutal?",
          answer: "Because you deserve the truth. Real estate agents sugarcoat everything. Marketing materials highlight only the positives. We don't. If there's road noise, we'll tell you. If crime rates are high, you'll know. If transport is terrible, we won't pretend it's 'developing'. That's literally the point of Let's Be Real: brutally honest, data-driven insights."
        },
        {
          question: "Do I need to create an account?",
          answer: "Nope! No signup, no login, no personal data required. Just enter an address and get your report. We don't track you, sell your data, or send spam emails. Simple as that."
        }
      ]
    },
    {
      category: "Data & Accuracy",
      questions: [
        {
          question: "What data sources do you use?",
          answer: "We pull from multiple sources: Australian Bureau of Statistics (Census 2021, SEIFA indexes, property sales, crime stats, building approvals), HERE APIs (real-time transit schedules for all Australian cities, places/amenities, routing/commute times), OpenStreetMap (road infrastructure), and ChatGPT for AI synthesis. Everything is cross-referenced and fact-checked."
        },
        {
          question: "Can I trust the AI analysis?",
          answer: "Our AI (ChatGPT) analyzes real data. It doesn't hallucinate or make things up. We've engineered our prompts to be 100% fact-based, and we cross-check outputs against source data. The AI provides interpretation and context, but all stats are grounded in real numbers. That said, always do your own research too."
        },
        {
          question: "How often is the data updated?",
          answer: "It depends on the source. Census data is from 2021 (next Census is 2026). HERE Transit API provides real-time transit schedules (updated daily). HERE Places API gives current amenity data. OpenStreetMap is community-updated. Property sales, crime stats, and building approvals are quarterly (2024 Q3). We fetch most data live per request, so you get the most current information available."
        },
        {
          question: "What if the data seems wrong?",
          answer: "Data can sometimes be incomplete or outdated, especially in rapidly changing areas. If you notice an error, please contact us at help.letsbereal@gmail.com with the address and specific issue. We take accuracy seriously and will investigate."
        }
      ]
    },
    {
      category: "Using the Platform",
      questions: [
        {
          question: "What if my address doesn't work?",
          answer: "Try these steps: (1) Include the full street number, (2) Add the suburb name and state (e.g., 'NSW', 'VIC'), (3) Check spelling. We're limited to Australian addresses. Some rural or very new developments might not have complete data coverage yet."
        },
        {
          question: "Why does it take 30 seconds to generate a report?",
          answer: "We're pulling data from multiple APIs in real-time: geocoding your address, fetching nearby amenities, calculating distances, analyzing demographics, and running AI analysis. Quality over speed. We'd rather take 30 seconds and give you accurate insights than rush and give you garbage."
        },
        {
          question: "Can I save or download reports?",
          answer: "Currently, reports are view-only in your browser. You can bookmark the page or take screenshots. We're considering adding PDF export and save functionality in the future based on user demand."
        },
        {
          question: "Can I compare multiple properties?",
          answer: "Not yet, but it's on our roadmap! For now, open multiple browser tabs to compare properties side-by-side. We're working on a proper comparison feature."
        },
        {
          question: "What areas do you cover?",
          answer: "We cover all Australian addresses with available data. Metropolitan areas (Sydney, Melbourne, Brisbane, Perth, Adelaide) have the most comprehensive data. Regional and rural areas might have limited amenity or transport data but will still show Census demographics and other available insights."
        }
      ]
    },
    {
      category: "Understanding Reports",
      questions: [
        {
          question: "What does 'Brutal Truth' mean?",
          answer: "It's our signature section, a no-BS summary that calls out the real pros and cons based on the data. We don't sugarcoat. If the property has issues (noise, crime, poor transport), we'll state it plainly. If it's great, we'll say that too. Think of it as what a brutally honest friend would tell you."
        },
        {
          question: "What's the difference between 'Investor Thought' and 'Owner Occupier Thought'?",
          answer: "Investor Thought analyzes the property from an investment lens: rental yield, capital growth potential, tenant appeal, and ROI. Owner Occupier Thought focuses on liveability: daily comfort, noise tolerance, lifestyle trade-offs, and long-term satisfaction. Same data, different perspectives."
        },
        {
          question: "What is the SEIFA score?",
          answer: "SEIFA (Socio-Economic Indexes for Areas) is an ABS index that ranks areas by socio-economic advantage. Higher scores = more affluent areas (higher income, education, employment). Lower scores = more disadvantage. It's one indicator of neighborhood quality, but not the whole story."
        },
        {
          question: "How do you calculate the 'Accessibility' score?",
          answer: "We look at proximity to transport (train stations, bus stops within 2km), essential amenities (supermarkets, cafes, restaurants, medical facilities within 1km), and schools. Closer means better score. We weight transport more heavily because it impacts daily life significantly."
        }
      ]
    },
    {
      category: "Privacy & Legal",
      questions: [
        {
          question: "Do you collect my data?",
          answer: "We only log the address you search for analytics purposes (to improve the service). We don't track personal information, create user profiles, or sell data to third parties. No cookies, no tracking pixels, no creepy surveillance. See our Privacy Policy for full details."
        },
        {
          question: "Can I use your reports for commercial purposes?",
          answer: "Reports are for personal use only. If you're a real estate agent, developer, or business wanting to use our data commercially, please contact us at help.letsbereal@gmail.com to discuss licensing."
        },
        {
          question: "Is this financial or legal advice?",
          answer: "Absolutely not. Our reports are informational and educational only. We're not licensed financial advisors, real estate agents, or lawyers. Always do your own due diligence, consult professionals, and make decisions based on your personal circumstances."
        }
      ]
    },
    {
      category: "Technical",
      questions: [
        {
          question: "What browsers do you support?",
          answer: "We support all modern browsers: Chrome, Firefox, Safari, Edge. Mobile browsers work great too. If you're using Internet Explorer... mate, it's 2025. Time to upgrade."
        },
        {
          question: "Is there an API I can use?",
          answer: "Not publicly available yet. If you're a developer interested in API access, reach out to us at help.letsbereal@gmail.com and tell us your use case."
        },
        {
          question: "Can I contribute to the project?",
          answer: "We're not open source (yet), but we welcome feedback, bug reports, and feature suggestions. Email us at help.letsbereal@gmail.com or find our contact details on the About page."
        }
      ]
    }
  ];

  // Filter FAQs based on search query
  const filteredFaqs = searchQuery
    ? faqs.map(category => ({
        ...category,
        questions: category.questions.filter(q =>
          q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
          q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqs;

  // Scroll to category
  const scrollToCategory = (categoryName: string) => {
    const ref = categoryRefs.current[categoryName];
    if (ref) {
      ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4 mb-4">
            <div className="p-2 sm:p-3 rounded-lg sm:rounded-xl bg-primary/10 flex-shrink-0">
              <HelpCircle className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-foreground">
                Frequently Asked Questions
              </h1>
              <p className="text-xs sm:text-sm md:text-base text-muted-foreground mt-0.5 sm:mt-1">
                Everything you need to know about Let's Be Real
              </p>
            </div>
          </div>
        </div>

        {/* Quick Answers Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
          <Card className="p-4 text-center border-2 hover:border-primary/50 transition-colors">
            <DollarSign className="h-6 w-6 text-green-500 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-primary">Free</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Always</div>
          </Card>
          <Card className="p-4 text-center border-2 hover:border-primary/50 transition-colors">
            <Zap className="h-6 w-6 text-amber-500 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-primary">30s</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Report time</div>
          </Card>
          <Card className="p-4 text-center border-2 hover:border-primary/50 transition-colors">
            <Database className="h-6 w-6 text-blue-500 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-primary">2021</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Census data</div>
          </Card>
          <Card className="p-4 text-center border-2 hover:border-primary/50 transition-colors">
            <Clock className="h-6 w-6 text-purple-500 mx-auto mb-2" />
            <div className="text-2xl sm:text-3xl font-bold text-primary">Live</div>
            <div className="text-xs sm:text-sm text-muted-foreground">Transit data</div>
          </Card>
        </div>

        {/* Search Filter */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search questions..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Category Pills */}
        <div className="sticky top-16 z-40 bg-background/95 backdrop-blur py-3 border-b mb-6 -mx-4 px-4 sm:-mx-6 sm:px-6">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {faqs.map((cat) => (
              <Button 
                key={cat.category}
                variant="outline" 
                size="sm"
                onClick={() => scrollToCategory(cat.category)}
                className="whitespace-nowrap flex-shrink-0"
              >
                {cat.category}
              </Button>
            ))}
          </div>
        </div>

        {/* FAQ Sections */}
        <div className="space-y-8">
          {filteredFaqs.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center">
                <p className="text-muted-foreground">No questions match your search.</p>
                <Button 
                  variant="link" 
                  onClick={() => setSearchQuery("")}
                  className="mt-2"
                >
                  Clear search
                </Button>
              </CardContent>
            </Card>
          ) : (
            filteredFaqs.map((category, categoryIndex) => (
              <div 
                key={categoryIndex} 
                ref={(el) => categoryRefs.current[category.category] = el}
                id={category.category.replace(/\s+/g, '-').toLowerCase()}
              >
                <h2 className="text-xl sm:text-2xl font-bold mb-4 text-foreground">
                  {category.category}
                </h2>
                
                <Accordion type="single" collapsible className="space-y-4">
                  {category.questions.map((faq, index) => (
                    <AccordionItem 
                      key={index} 
                      value={`category-${categoryIndex}-item-${index}`}
                      className="border rounded-lg px-4 bg-card"
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-4">
                        <span className="font-semibold text-base sm:text-lg">
                          {faq.question}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-4 text-sm sm:text-base leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))
          )}
        </div>

        {/* Still Have Questions CTA */}
        <Card className="mt-12 bg-primary/5 border-primary/20">
          <CardContent className="pt-6 text-center">
            <h3 className="text-lg sm:text-xl font-semibold mb-2">Still Have Questions?</h3>
            <p className="text-sm sm:text-base text-muted-foreground mb-4">
              Can't find what you're looking for? We're here to help!
            </p>
            <Button 
              onClick={() => navigate("/about")}
              className="h-10 sm:h-12 text-sm sm:text-base"
            >
              Contact Us
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FAQ;
